import { isDefined, isNotDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectAlgoNotionalCurrencyIndex,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/fixing/selectors.ts';

type AlgoNotionalProps = {
  notional: number | undefined;
  setNotional: (value: number | undefined) => void;
};

export const AlgoEditingNotional = (props: AlgoNotionalProps) => {
  const { notional, setNotional } = props;
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const track = useTrackEvent();

  const notionalCurrencyIndex = useAppSelector((state) => selectAlgoNotionalCurrencyIndex(state, tileId));
  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const notionalError = errors?.notional;

  const currency = isDefined(orderFromBlotter) ? orderFromBlotter?.notionalCurrency : undefined;

  const validateAlgo = useValidateAlgo();

  const handleNotionalChange = async (value: number | undefined) => {
    track('Order action', 'Notional changed');
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : [];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleNotionalChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateAlgo({
        notional: value,
        notionalCurrencyIndex: currency === currency1 ? 1 : 2,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setNotional(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.notional.label">
      <OmsFieldErrorWrapper errors={notionalError}>
        <SgAmountInput
          size="sm"
          value={
            notional ?? (isDefined(orderFromBlotter?.notional) ? (orderFromBlotter?.notional as number) : undefined)
          }
          data-e2e="notional"
          onChange={handleNotionalChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          unit={currency}
          state={isDefined(notionalError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
